/**
 * Browser-side facade for auth operations.
 *
 * The browser Supabase client singleton lives in `@/lib/supabase/client` (the
 * Supabase boundary) — this file only consumes it and exposes auth ops that
 * delegate to the framework-agnostic adapters in `./auth`. No `@supabase/*`
 * import here.
 *
 * Throw-canonical: these ops resolve on success and THROW a typed SignalError
 * on failure (same as the server adapters they delegate to).
 *
 * Usage (from a "use client" component):
 *   import { signInWithEmail, signOut, refreshSession } from "@/lib/auth/browser";
 */

import { getRawBrowserClient } from "@/lib/supabase/client";
import {
  exchangeCodeForSession as exchangeCodeAdapter,
  getCurrentUserOrNull as getCurrentUserOrNullAdapter,
  refreshSession as refreshSessionAdapter,
  signInWithPassword as signInWithPasswordAdapter,
  signOut as signOutAdapter,
  signUp as signUpAdapter,
} from "./auth";
import type { AuthUser } from "./types";

export type { AuthUser };
// Re-export the client accessor so existing import sites keep working.
export { getRawBrowserClient };

export interface SignInWithEmailParams {
  email: string;
  password: string;
}

export interface SignUpWithEmailParams extends SignInWithEmailParams {
  invite: string;
  accountKind: "personal" | "organization";
  organizationType?:
    "business" | "nonprofit" | "government" | "education" | "other";
  next?: string;
}

/** Sign in with email + password. Resolves on success; throws a typed error. */
export async function signInWithEmail(
  params: SignInWithEmailParams,
): Promise<void> {
  return signInWithPasswordAdapter(
    getRawBrowserClient(),
    params.email,
    params.password,
  );
}

/**
 * Sign up with email + password. Returns whether the project requires email
 * confirmation (no session yet) — the caller shows "check your inbox".
 */
export async function signUpWithEmail(
  params: SignUpWithEmailParams,
): Promise<{ needsEmailConfirmation: boolean }> {
  const site = typeof window !== "undefined" ? window.location.origin : "";
  const next = params.next ? `?next=${encodeURIComponent(params.next)}` : "";
  return signUpAdapter(getRawBrowserClient(), params.email, params.password, {
    emailRedirectTo: `${site}/auth/confirm${next}`,
    data: {
      beta_invite: params.invite,
      filium_account_kind: params.accountKind,
      ...(params.organizationType
        ? { filium_organization_type: params.organizationType }
        : {}),
    },
  });
}

/** Complete a PKCE email link (?code=) — signup confirm, magic link, recovery. */
export async function exchangeCodeForSession(code: string): Promise<void> {
  return exchangeCodeAdapter(getRawBrowserClient(), code);
}

/** The current browser-session user, or null when signed out. */
export async function getCurrentUserOrNull(): Promise<AuthUser | null> {
  return getCurrentUserOrNullAdapter(getRawBrowserClient());
}

/** Sign the user out — clears cookies, drops the local session. */
export async function signOut(): Promise<void> {
  return signOutAdapter(getRawBrowserClient());
}

/** Re-issue the access token using the existing refresh token. */
export async function refreshSession(): Promise<AuthUser> {
  return refreshSessionAdapter(getRawBrowserClient());
}
